class QuizValidator {
  constructor(config = {}) {
    this.config = config;
    this.translations = {};
    this.loadTranslations();
  }

  loadTranslations() {
    const translationScript = document.querySelector('script[data-quiz-translations]');
    if (translationScript) {
      try {
        const content = translationScript.textContent.trim();
        if (content) {
          this.translations = JSON.parse(content);
          console.log('Loaded translations:', this.translations);
        } else {
          console.warn('Empty translation script content');
        }
      } catch (e) {
        console.warn('Failed to load quiz translations:', e);
        console.warn('Translation script content:', translationScript.textContent);
      }
    } else {
      console.warn('Translation script not found');
    }
  }

  getTranslation(key, replacements = {}) {
    const keys = key.split('.');
    let value = this.translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        console.warn(`Translation key not found: ${key}`);
        return this.getFallbackTranslation(key, replacements);
      }
    }
    
    if (typeof value === 'string') {
      return this.replaceVariables(value, replacements);
    }
    
    return this.getFallbackTranslation(key, replacements);
  }

  getFallbackTranslation(key, replacements = {}) {
    const fallbacks = {
      'validation.dog_name.required': 'Dog name is required',
      'validation.dog_name.min_length': 'Dog name must be at least {{min_length}} characters',
      'validation.dog_name.max_length': 'Dog name cannot exceed {{max_length}} characters',
      'validation.dog_weight.required': 'Dog weight is required',
      'validation.dog_weight.min_weight': 'Dog weight must be at least {{min_weight}} kg',
      'validation.dog_weight.max_weight': 'Dog weight cannot exceed {{max_weight}} kg',
      'validation.dog_weight.invalid_format': 'Please enter a valid weight',
      'validation.dog_weight.decimal_not_allowed': 'Weight must be a whole number',
      'validation.email.required': 'Email address is required',
      'validation.email.invalid_format': 'Please enter a valid email address',
      'validation.feeding_style.required': 'Please select a feeding style',
      'validation.pack_size.required': 'Please select a pack size',
      'validation.recipes.required': 'Please select at least one recipe',
      'validation.recipes.exact_count': 'Please select exactly {{count}} meals',
      'validation.recipes.insufficient_count': 'Please select {{remaining}} more meals',
      'validation.dogs.required': 'Please add at least one dog',
      'validation.general.form_invalid': 'Please correct the errors below',
      'validation.general.required_field': 'This field is required',
      'validation.age_group.invalid': 'Please select a valid age group',
      'validation.puppy_range.invalid': 'Please select a valid puppy age range'
    };
    
    const fallback = fallbacks[key] || key;
    return this.replaceVariables(fallback, replacements);
  }

  replaceVariables(text, replacements) {
    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return replacements[key] !== undefined ? replacements[key] : match;
    });
  }

  validateDogName(name) {
    const errors = [];
    const config = this.config.validation?.dog_name || {};
    
    if (!name || name.trim() === '') {
      errors.push(this.getTranslation('validation.dog_name.required'));
      return { isValid: false, errors };
    }
    
    const trimmedName = name.trim();
    
    if (trimmedName.length < config.min_length) {
      errors.push(this.getTranslation('validation.dog_name.min_length', {
        min_length: config.min_length
      }));
    }
    
    if (trimmedName.length > config.max_length) {
      errors.push(this.getTranslation('validation.dog_name.max_length', {
        max_length: config.max_length
      }));
    }
    

    
    return {
      isValid: errors.length === 0,
      errors
    };
  }


  validateDogWeight(weight) {
    const errors = [];
    const config = this.config.validation?.dog_weight || {};
    
    if (weight === null || weight === undefined || weight === '') {
      errors.push(this.getTranslation('validation.dog_weight.required'));
      return { isValid: false, errors };
    }
    
    const numericWeight = parseFloat(weight);
    
    if (isNaN(numericWeight)) {
      errors.push(this.getTranslation('validation.dog_weight.invalid_format'));
      return { isValid: false, errors };
    }
    
    if (!config.allow_decimal && numericWeight !== Math.floor(numericWeight)) {
      errors.push(this.getTranslation('validation.dog_weight.decimal_not_allowed'));
    }
    
    if (numericWeight < config.min_weight) {
      errors.push(this.getTranslation('validation.dog_weight.min_weight', {
        min_weight: config.min_weight
      }));
    }
    
    if (numericWeight > config.max_weight) {
      errors.push(this.getTranslation('validation.dog_weight.max_weight', {
        max_weight: config.max_weight
      }));
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  validateEmail(email) {
    const errors = [];
    const config = this.config.validation?.email || {};
    
    if (!email || email.trim() === '') {
      if (config.required) {
        errors.push(this.getTranslation('validation.email.required'));
      }
      return { isValid: !config.required, errors };
    }
    
    const emailRegex = new RegExp(config.pattern || '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$');
    if (!emailRegex.test(email.trim())) {
      errors.push(this.getTranslation('validation.email.invalid_format'));
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  validateAgeGroup(ageGroup) {
    const errors = [];
    const config = this.config.validation?.age_group || {};
    
    if (!ageGroup || ageGroup.trim() === '') {
      errors.push(this.getTranslation('validation.age_group.required'));
      return { isValid: false, errors };
    }
    
    const validAgeGroups = ['adult', 'puppy', 'senior'];
    if (!validAgeGroups.includes(ageGroup)) {
      errors.push(this.getTranslation('validation.age_group.invalid'));
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  validatePuppyRange(puppyRange) {
    const errors = [];
    const config = this.config.validation?.puppy_range || {};
    
    // If puppy range is empty and not required, it's valid
    if (!puppyRange || puppyRange.trim() === '') {
      if (config.required) {
        errors.push(this.getTranslation('validation.puppy_range.required'));
        return { isValid: false, errors };
      } else {
        return { isValid: true, errors: [] };
      }
    }
    
    const validPuppyRanges = ['0-3', '3-6', '6-9', '9-12'];
    if (!validPuppyRanges.includes(puppyRange)) {
      errors.push(this.getTranslation('validation.puppy_range.invalid'));
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  validateDogProfile(dogData) {
    const errors = {};
    let isValid = true;
    
    const nameValidation = this.validateDogName(dogData.name);
    if (!nameValidation.isValid) {
      errors.name = nameValidation.errors;
      isValid = false;
    }
    
    const ageGroupValidation = this.validateAgeGroup(dogData.age_group);
    if (!ageGroupValidation.isValid) {
      errors.age_group = ageGroupValidation.errors;
      isValid = false;
    }
    
    // Only validate puppy range if age group is puppy
    if (dogData.age_group === 'puppy') {
      const puppyRangeValidation = this.validatePuppyRange(dogData.puppy_range);
      if (!puppyRangeValidation.isValid) {
        errors.puppy_range = puppyRangeValidation.errors;
        isValid = false;
      }
    }
    
    const weightValidation = this.validateDogWeight(dogData.weight);
    if (!weightValidation.isValid) {
      errors.weight = weightValidation.errors;
      isValid = false;
    }
    
    return {
      isValid,
      errors,
      message: isValid ? null : this.getTranslation('validation.general.form_invalid')
    };
  }

  validateFeedingStyle(feedingStyle) {
    const errors = [];
    
    if (!feedingStyle || feedingStyle.trim() === '') {
      errors.push(this.getTranslation('validation.feeding_style.required'));
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  validatePackSize(packSize) {
    const errors = [];
    
    if (!packSize || packSize.trim() === '') {
      errors.push(this.getTranslation('validation.pack_size.required'));
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  validateRecipeSelection(selectedRecipes, requiredCount) {
    const errors = [];
    
    if (!selectedRecipes || selectedRecipes.length === 0) {
      errors.push(this.getTranslation('validation.recipes.required'));
    } else {
      const totalMeals = selectedRecipes.reduce((sum, recipe) => sum + (recipe.quantity || 0), 0);
      
      if (totalMeals !== requiredCount) {
        if (totalMeals < requiredCount) {
          const remaining = requiredCount - totalMeals;
          errors.push(this.getTranslation('validation.recipes.insufficient_count', {
            remaining: remaining
          }));
        } else {
          errors.push(this.getTranslation('validation.recipes.exact_count', {
            count: requiredCount
          }));
        }
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  validateDogsList(dogs) {
    const errors = [];
    
    if (!dogs || dogs.length === 0) {
      errors.push(this.getTranslation('validation.dogs.required'));
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  validateStep(stepNumber, data) {
    switch (stepNumber) {
      case 1:
        return this.validateDogsList(data.dogs);
      case 2:
        return this.validateFeedingStyle(data.feeding_style);
      case 4:
        return this.validateEmail(data.email_address);
      case 5:
        return this.validatePackSize(data.selected_pack_size);
      case 6:
        const packSize = parseInt(data.selected_pack_size?.split(' ')[0] || '0');
        return this.validateRecipeSelection(data.selected_recipes, packSize);
      default:
        return { isValid: true, errors: [] };
    }
  }
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = QuizValidator;
} 